import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { Link, useLoaderData, useNavigate } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SyncIcon from "@mui/icons-material/Sync";

import { toast } from "react-toastify";
import DefaultApiService from "../../../../../infrastructure/api/default";
import useCheckPermission from "../../../../common/hooks/useCheckPermission";
import useGetPageInfo from "../../../../common/hooks/useGetPageInfo";
import DeleteConfirm from "../../../../components/shared/Dialog/DeleteConfirm";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import EnhancedTable from "../../../../components/shared/table";
import useSupervisorLocationFilters, {
  SupervisorLocationFilterBar,
} from "../locationFilters";

const SolarentOperatorRequestPage = () => {
  const pageInfo = useGetPageInfo();

  const dataList = useLoaderData("root");
  const {
    provinceFilter,
    cityFilter,
    provinces,
    filteredCities,
    setProvinceFilter,
    setCityFilter,
    matchesLocation,
  } = useSupervisorLocationFilters();
  const filteredRows = useMemo(
    () => (Array.isArray(dataList) ? dataList.filter(matchesLocation) : []),
    [dataList, matchesLocation]
  );

  let navigate = useNavigate();
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    data: {},
    cascadeDelete: false,
  });
  const headCells = [
    {
      id: "updateOn",
      label: "تاریخ",
      type: "date",
    },
   
    {
      id: "projectOwnerFirstName",
      label: "نام",
    },
    {
      id: "projectOwnerLastName",
      label: "نام خانوادگی",
    },
    
    {
      id: "projectTitle",
      label: "عنوان پروژه",
    },
    {
      id: "referenceID",
      label: "شناسه درخواست",
    },
    {
      id: "expireDate",
      label: "تاریخ اعتبار",
      type: "date",
    },
    {
      id: "adminComment",
      label: "یادداشت مدیر",
    },
    {
      id: "agreementKind",
      label: "نوع توافق",
    },
    {
      id: "projectKind",
      label: "نوع درخواست",
    },
    {
      id: "requestConfirm",
      label: "وضعیت درخواست",
    },
    {
      id: "cityName",
      label: "شهرستان",
    },
    {
      id: "substationName",
      label: "ایستگاه",
    },
    {
      id: "feederName",
      label: "فیدر",
    },
    {
      id: "stepTitle",
      label: "مرحله فعلی",
    },
     
  ];

  const onEditClicked = (row) => {
    if (row.requestConfirm === "NotChecked") navigate(`${row.id}`);
    else {
      toast.warn("فقط امکان ویرایش درخواست هایی که چک نشده اند وجود دارد.");
    }
  };

  const onCheckRequestClicked = (row) => {
    if (row.requestConfirm !== "NotChecked") {
      navigate(`Information?id=${row.id}`);
    } else {
      toast.warn("فقط امکان بررسی درخواست هایی که چک شده اند وجود دارد.");
    }
  };

  const onDeleteClicked = (row) => {
    setDeleteConfirm((old) => ({ ...old, open: true, data: row }));
  };

  const confirmedDelete = () => {
    return DefaultApiService.deleteOneApi("SLN/SolarentOperator/Request", {
      id: deleteConfirm.data.id,
      cascadeDelete: deleteConfirm.cascadeDelete,
    });
  };

  const onFetchData = () => {
    navigate();
  };

  const getRowStatus = (row) => {
    switch (row.requestConfirm) {
      case "NotChecked":
        return "gray";
      case "Canceled":
        return "danger";
      case "InProcess":
        return "primary";
      case "Completed":
        return "success";
      default:
        return "warning";
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        <BreadcrumbsComponent />

        <DeleteConfirm
          message={`برای حذف "${deleteConfirm.data.feederName}" اطمینان دارید؟`}
          open={deleteConfirm.open}
          onClose={(loadAgain) => {
            setDeleteConfirm({
              open: false,
              data: {},
              cascadeDelete: false,
            });
            if (loadAgain) {
              navigate();
            }
          }}
          onSubmit={confirmedDelete}
        >
          <FormControlLabel
            name="cascadeDelete"
            checked={deleteConfirm.cascadeDelete}
            onChange={(event) => {
              setDeleteConfirm((old) => ({
                ...old,
                cascadeDelete: event.target.checked,
              }));
            }}
            control={<Switch color="danger" />}
            label="حذف از تمامی جاهایی که استفاده شده است"
          />
        </DeleteConfirm>
      </Grid>

      <Grid item xs={12} md={12}>
        <Card>
          <CardHeader
            title={pageInfo.title}
            action={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "fit-content",
                }}
              >
                {useCheckPermission("create") && (
                  <Link to={"create"}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                    >
                      ایجاد
                    </Button>
                  </Link>
                )}

                <Divider
                  orientation="vertical"
                  variant="middle"
                  flexItem
                  sx={{ mx: 1 }}
                />

                <IconButton
                  variant="text"
                  color="primary"
                  onClick={onFetchData}
                >
                  <SyncIcon />
                </IconButton>
              </Box>
            }
          />

          <CardContent>
            <SupervisorLocationFilterBar
              provinceFilter={provinceFilter}
              cityFilter={cityFilter}
              provinces={provinces}
              filteredCities={filteredCities}
              onProvinceChange={setProvinceFilter}
              onCityChange={setCityFilter}
            />
            <Grid container>
              <Grid item xs={12} md={12}>
                <EnhancedTable
                  rowKey={"id"}
                  rows={filteredRows}
                  headCells={headCells}
                  defaultSort={{ orderBy: "updateOn", order: "desc" }}
                  pageSize={100}
                  actions={[
                    {
                      icon: useCheckPermission("edit") ? (
                        <ContentPasteSearchIcon />
                      ) : null,
                      onClick: onCheckRequestClicked,
                      color: "primary",
                      tooltip: "بررسی",
                    },
                    {
                      icon: useCheckPermission("edit") ? (
                        <EditIcon sx={{ width: "1rem", height: "1rem" }} />
                      ) : null,
                      onClick: onEditClicked,
                      color: "primary",
                    },
                    {
                      icon: useCheckPermission("delete") ? (
                        <DeleteIcon sx={{ width: "1rem", height: "1rem" }} />
                      ) : null,
                      onClick: onDeleteClicked,
                      color: "secondary",
                    },
                  ]}
                  getRowStatus={getRowStatus}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default SolarentOperatorRequestPage;
